import { onScopeDispose, watch } from "vue";

import { useAudioFocus } from "@/composables/use-audio-focus.composable";
import type { UsePlayerReturn } from "@/composables/use-player.composable";

const audioFocus = useAudioFocus();

const actionHandlers = {
  play: () => void audioFocus.focusedPlayer.value?.play(),
  pause: () => audioFocus.focusedPlayer.value?.pause(),
  nexttrack: () => void audioFocus.focusedPlayer.value?.skipForward(),
  previoustrack: () => void audioFocus.focusedPlayer.value?.skipBackward(),
  seekforward: ({ seekOffset }) =>
    audioFocus.focusedPlayer.value?.seekForward(seekOffset),
  seekbackward: ({ seekOffset }) =>
    audioFocus.focusedPlayer.value?.seekBackward(seekOffset),
  seekto: ({ seekTime }) => {
    if (seekTime !== undefined) {
      audioFocus.focusedPlayer.value?.seekTo(seekTime);
    }
  },
} satisfies Partial<Record<MediaSessionAction, MediaSessionActionHandler>>;

function registerActionHandlers() {
  const { mediaSession } = navigator;

  Object.entries(actionHandlers).forEach(([action, handler]) => {
    mediaSession.setActionHandler(
      action as keyof typeof actionHandlers,
      handler,
    );
  });
}

function reset() {
  const { mediaSession } = navigator;

  mediaSession.metadata = null;
  mediaSession.playbackState = "none";
  mediaSession.setPositionState();

  Object.keys(actionHandlers).forEach((action) => {
    mediaSession.setActionHandler(action as keyof typeof actionHandlers, null);
  });
}

export function useMediaSession(player: UsePlayerReturn): void {
  let ownsMediaSession = false;

  watch(
    [audioFocus.focusedPlayer, player.currentTrack],
    ([focusedPlayer, track]) => {
      if (focusedPlayer !== player) {
        return;
      }

      navigator.mediaSession.metadata = track
        ? new MediaMetadata({
            title: track.trackName,
            artist: track.artistName,
            album: track.collectionName,
            artwork: [{ src: track.artworkUrl }],
          })
        : null;
    },
    { flush: "sync" },
  );

  watch(
    [
      audioFocus.focusedPlayer,
      player.isPlaybackRequested,
      player.duration,
      player.timeElapsed,
    ],
    ([focusedPlayer, isPlaybackRequested, duration, position]) => {
      if (focusedPlayer !== player) {
        if (ownsMediaSession && !focusedPlayer) {
          reset();
        }

        ownsMediaSession = false;
        return;
      }

      if (!ownsMediaSession) {
        registerActionHandlers();
      }

      ownsMediaSession = true;

      const { mediaSession } = navigator;

      mediaSession.playbackState = isPlaybackRequested ? "playing" : "paused";
      mediaSession.setPositionState(
        duration !== undefined && duration > 0 && position !== undefined
          ? {
              duration,
              playbackRate: 1,
              position: Math.min(Math.max(0, position), duration),
            }
          : undefined,
      );
    },
    { flush: "sync" },
  );

  onScopeDispose(() => {
    const focusedPlayer = audioFocus.focusedPlayer.value;

    if (ownsMediaSession && (!focusedPlayer || focusedPlayer === player)) {
      reset();
    }
  });
}
